import React, { Component } from 'react';
import PropTypes from 'prop-types';
import classNames from 'classnames';
import noop from 'lodash/noop';
import Tooltip from '@adsk/bim360-matrix-react-tooltip';
import OverflowTooltip from '@adsk/bim360-matrix-react-overflow-tooltip';
import escapeRegexString from '@adsk/bim360-matrix-react-helpers/es/escapeRegexString';

import './_SearchBoxSuggestedResults.scss';

/**
 * SearchBoxSuggestedResults - stateless component that renders the suggested results drop down
 */
export default class SearchBoxSuggestedResults extends Component {
  /**
   * @constructor
   **/
  constructor(props) {
    super(props);
    this._renderHighlightedTitle = this._renderHighlightedTitle.bind(this);
    this._renderSuggestedCells = this._renderSuggestedCells.bind(this);
  }

  _renderHighlightedTitle(title) {
    const searchExpression = this.props.searchExpression.toLowerCase();
    const splittedText = title.split(
      new RegExp('(' + escapeRegexString(this.props.searchExpression) + ')', 'ig')
    );

    return splittedText.map((partOfTitle, index) => {
      return (
        <span
          key={index}
          className={
            partOfTitle.toLowerCase() === searchExpression
              ? 'SearchBoxSuggestedResults__highlighted-search'
              : ''
          }
        >
          {partOfTitle}
        </span>
      );
    });
  }

  _renderSuggestedCells() {
    return this.props.suggestionList.map((currSuggestion, index) => {
      const containerClasses = classNames('SearchBoxSuggestedResults__suggestion-cell-container', {
        'SearchBoxSuggestedResults__suggestion-cell-container--selected':
          index === this.props.currentSelectedRow - 1
      });
      const currSuggestionText = this.props.suggestionDataGetter(currSuggestion);

      return (
        <OverflowTooltip
          key={index}
          content={currSuggestionText}
          theme={Tooltip.Themes.LIGHT}
          autoTipOnEllipsis={true}
        >
          <div
            className={containerClasses}
            onClick={() => this.props.onSuggestedResultRowSelect(currSuggestion)}
            onMouseOver={() => this.props.setCurrentSelectedRow(index)}
          >
            {this.props.suggestionPrefixRenderer(currSuggestion)}
            <div
              className="SearchBoxSuggestedResults__suggestion-cell"
              data-testid={`SearchBoxResult${index}`}
            >
              {this.props.searchExpression.length > 1
                ? this._renderHighlightedTitle(currSuggestionText)
                : currSuggestionText}
            </div>
          </div>
        </OverflowTooltip>
      );
    });
  }

  render() {
    if (!this.props.isVisible) {
      return null;
    }
    return (
      <div
        className="SearchBoxSuggestedResults"
        onMouseLeave={() => this.props.resetSelection()}
        ref={n => {
          this.resultsContainer = n;
        }}
      >
        {this._renderSuggestedCells()}
      </div>
    );
  }
}

SearchBoxSuggestedResults.defaultProps = {
  suggestionDataGetter: suggestion => suggestion.title,
  suggestionPrefixRenderer: noop
};

SearchBoxSuggestedResults.propTypes = {
  /**
   * Whether to component should be visible
   */
  isVisible: PropTypes.bool,
  /**
   * The index of the current selected row starting from 1
   */
  currentSelectedRow: PropTypes.number, // See comment in SearchBoxDropdown constructor for more details
  /**
   * A callback function for the event of setting a suggestion as selected
   */
  setCurrentSelectedRow: PropTypes.func.isRequired,
  /**
   * A callback function for the event of selecting a suggestion
   */
  onSuggestedResultRowSelect: PropTypes.func.isRequired,
  /**
   * The suggestions list
   */
  suggestionList: PropTypes.array.isRequired,
  /**
   * A function that receives the suggestionList member and returns the suggested result text
   */
  suggestionDataGetter: PropTypes.func,
  /**
   * A function that returns a React component. Will be rendered as the prefix for the suggested result
   * Receives as a parameter the current suggestionList member
   */
  suggestionPrefixRenderer: PropTypes.func,
  /**
   * The current search expression text
   */
  searchExpression: PropTypes.string.isRequired,
  /**
   * Lets parent know when hovering out of the results, thus resetting any selection
   */
  resetSelection: PropTypes.func.isRequired
};
